import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { reportError } from '../../services/errorReportingService';
import { translations, defaultLanguage } from '../../i18n';
import logo from '../../assets/logo.png';

/**
 * Get user language from localStorage (outside React context)
 */
const getUserLanguage = () => {
    try {
        const cached = localStorage.getItem('cabazaPreferencesCache');
        if (cached) {
            const prefs = JSON.parse(cached);
            if (prefs.language && translations[prefs.language]) return prefs.language;
        }
    } catch (e) { /* ignore */ }
    return defaultLanguage;
};

/**
 * React 19 Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * and automatically reports them to the support system in production.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            isReporting: false,
            reportSent: false
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error,
            errorInfo,
            isReporting: true
        });

        // Report error to support system (only in production)
        reportError(error, errorInfo, { source: 'react' })
            .then((success) => {
                this.setState({
                    isReporting: false,
                    reportSent: success
                });
            })
            .catch(() => {
                this.setState({ isReporting: false });
            });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            isReporting: false,
            reportSent: false
        });
    };

    handleGoHome = () => {
        // Reset to home page to avoid reloading the page that caused the error
        localStorage.setItem('cabaza_dashboard_page', 'home');
        // Force a full page reload to clear React state
        window.location.replace('/');
    };

    render() {
        if (this.state.hasError) {
            const lang = getUserLanguage();
            const t = translations[lang] || translations[defaultLanguage];
            const err = t.errorBoundary || {};

            return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                    <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8">
                        {/* Logo */}
                        <div className="flex items-center justify-center mb-6">
                            <img src={logo} alt="Cabaza" className="h-14 object-contain" />
                        </div>

                        {/* Title */}
                        <h1 className="text-3xl font-bold text-gray-900 text-center mb-4 flex items-center justify-center gap-2">
                            <AlertTriangle className="w-7 h-7 text-red-500 flex-shrink-0" />
                            {err.title || "Oops! An error occurred"}
                        </h1>

                        {/* Description */}
                        <p className="text-gray-600 text-center mb-6">
                            {err.description || "Something went wrong. Our engineers have been notified and are working on a solution."}
                        </p>

                        {/* Report status */}
                        {import.meta.env.PROD && (
                            <div className="text-center mb-6">
                                {this.state.isReporting && (
                                    <p className="text-sm text-blue-600">
                                        {err.sendingReport || "Sending error report..."}
                                    </p>
                                )}
                                {this.state.reportSent && (
                                    <p className="text-sm text-green-600">
                                        {err.reportSent || "A ticket has been automatically created for this issue."}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Error details (only in development) */}
                        {import.meta.env.DEV && this.state.error && (
                            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
                                <h2 className="text-sm font-semibold text-red-800 mb-2">
                                    {err.devDetails || "Error details (development mode):"}
                                </h2>
                                <pre className="text-xs text-red-700 overflow-x-auto whitespace-pre-wrap">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={this.handleReset}
                                className="bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                            >
                                <RefreshCw className="w-5 h-5" />
                                {err.retry || "Retry"}
                            </button>
                            <button
                                onClick={this.handleGoHome}
                                className="bg-white text-cabaza-purple border-2 border-cabaza-purple px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                            >
                                <Home className="w-5 h-5" />
                                {err.backHome || "Back to home"}
                            </button>
                        </div>

                        {/* Additional help */}
                        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                            <p className="text-sm text-gray-500">
                                {err.persistHelp || "If the problem persists, please contact technical support."}
                            </p>
                            <p className="text-xs text-gray-400 mt-3">
                                &copy; Cabaza {new Date().getFullYear()} — {err.allRightsReserved || "All rights reserved"}
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Functional wrapper for ErrorBoundary with custom fallback
 * This allows you to use custom error UI if needed
 */
export const withErrorBoundary = (Component, customFallback) => {
    return (props) => (
        <ErrorBoundary fallback={customFallback}>
            <Component {...props} />
        </ErrorBoundary>
    );
};

export default ErrorBoundary;
